import React from 'react';

/**
 * Mandate-style rich text for scorecard bodies:
 * - Lines starting with `**Label:**` become hardcoded subheading blocks (no visible **)
 * - Remaining `**words**` render as bold (no visible **)
 * - Bullet lines (`* ` / `- ` / `• `) render as list items
 *
 * Overall and Detailed feedback use the same body typography (text-sm).
 */

const BODY_CLASS = 'text-sm leading-relaxed text-white/85 whitespace-pre-wrap';
const LABEL_CLASS = 'text-sm font-semibold text-white/90';

function inlineBold(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const m = remaining.match(/\*\*(.+?)\*\*/);
    if (m && m.index !== undefined) {
      if (m.index > 0) {
        parts.push(<span key={key++}>{remaining.slice(0, m.index)}</span>);
      }
      parts.push(
        <strong key={key++} className="font-semibold text-white/90">
          {m[1]}
        </strong>
      );
      remaining = remaining.slice(m.index + m[0].length);
    } else {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
  }
  return <>{parts}</>;
}

/** Strip a leading `**…Detailed Feedback**` line (UI already labels the section). */
export function stripDetailedFeedbackHeader(text: string): string {
  let out = text.trim();
  for (let i = 0; i < 5; i += 1) {
    const next = out
      .replace(
        /^\*\*[^*]*Detailed(?:\s+(?:Originality|Target\s+Market))?(?:\s+(?:User\s+)?)?Feedback[^*]*\*\*\s*/i,
        '',
      )
      .trim();
    if (next === out) break;
    out = next;
  }
  return out;
}

/**
 * Ensure every `**Label:**` subhead starts on its own paragraph.
 * Recovers carriage returns when backend sanitizers previously collapsed newlines.
 */
export function ensureSubheadBreaks(text: string): string {
  let out = text.replace(/\r\n/g, '\n');
  // Split before structural **Label:** markers that sit mid-paragraph.
  out = out.replace(/([^\n])[ \t]*(\*\*[^*]+?:\*\*)/g, '$1\n\n$2');
  // Normalize excess blank lines.
  out = out.replace(/\n{3,}/g, '\n\n');
  return out.trim();
}

type Block =
  | { kind: 'sub'; label: string; body: string }
  | { kind: 'para'; text: string }
  | { kind: 'list'; items: string[] };

/** Only `**Label:**` (colon inside the bold markers) is a structural subheading. */
const SUBHEAD_RE = /^\*\*([^*]+?):\*\*\s*(.*)$/;

export function parseBlocks(text: string): Block[] {
  const lines = ensureSubheadBreaks(text).split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let listItems: string[] = [];
  let currentSub: { label: string; body: string[] } | null = null;

  const flushPara = () => {
    if (!para.length) return;
    const joined = para.join('\n').trim();
    para = [];
    if (!joined) return;
    if (currentSub) {
      currentSub.body.push(joined);
    } else {
      blocks.push({ kind: 'para', text: joined });
    }
  };

  const flushList = () => {
    if (!listItems.length) return;
    const items = listItems.splice(0, listItems.length);
    if (currentSub) {
      currentSub.body.push(items.map((i) => `• ${i}`).join('\n'));
    } else {
      blocks.push({ kind: 'list', items });
    }
  };

  const flushSub = () => {
    if (!currentSub) return;
    blocks.push({
      kind: 'sub',
      label: currentSub.label.trim(),
      body: currentSub.body.join('\n\n').trim(),
    });
    currentSub = null;
  };

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) {
      flushList();
      flushPara();
      continue;
    }

    const bullet = trimmed.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      flushPara();
      listItems.push(bullet[1].trim());
      continue;
    }

    const sub = trimmed.match(SUBHEAD_RE);
    if (sub) {
      flushList();
      flushPara();
      flushSub();
      const rest = (sub[2] || '').trim();
      currentSub = {
        label: sub[1].trim(),
        body: rest ? [rest] : [],
      };
      continue;
    }

    flushList();
    para.push(trimmed);
  }

  flushList();
  flushPara();
  flushSub();
  return blocks;
}

type ScorecardRichTextProps = {
  text: string;
  className?: string;
};

export function ScorecardRichText({ text, className }: ScorecardRichTextProps) {
  const cleaned = stripDetailedFeedbackHeader(text || '');
  if (!cleaned) return null;

  const blocks = parseBlocks(cleaned);

  return (
    <div className={className ?? 'space-y-4'}>
      {blocks.map((b, i) => {
        if (b.kind === 'sub') {
          return (
            <div key={i} className="space-y-2">
              <p className={LABEL_CLASS}>{b.label}</p>
              {b.body ? <p className={BODY_CLASS}>{inlineBold(b.body)}</p> : null}
            </div>
          );
        }
        if (b.kind === 'list') {
          return (
            <ul key={i} className="space-y-2">
              {b.items.map((item, j) => (
                <li key={j} className={`${BODY_CLASS} border-l-2 border-white/10 pl-3`}>
                  {inlineBold(item)}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className={BODY_CLASS}>
            {inlineBold(b.text)}
          </p>
        );
      })}
    </div>
  );
}

export default ScorecardRichText;
